import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Input, Switch, InputNumber, Toast, Spin, Select, Checkbox, Pagination } from '@douyinfe/semi-ui';
import { API, apiErrMsg } from '../helpers';
import { navigate } from '../router';
import { usePagedRows } from '../helpers/usePagedRows';

function maskKey(k) {
  if (!k) return '';
  return k.slice(0, 8) + '...' + k.slice(-8);
}

// 限速单元格:直接显示**当前速度**(相对上游原速的百分比)。
// 口径与限流器一致(单卡近 5h 已用 / 该卡套餐 5h 限额):
//   5h 用量 0/1000 → 100%(上游原速)
//   5h 用量满      → 降到保底速度
// 所以这一列的数字就是"这张卡现在能跑多快",不用再叠一个进度箭头。
//
// ⚠️ 刻意用百分比而不是 T/S 绝对值:
//   上游真实速率实测 P50=31.9 / P90=107.8 / max=255 T/S,同一张卡不同请求
//   差好几倍,写死一个 T/S 数字既不准确也看不懂。
const ThrottleCell = ({ th }) => {
  if (!th) return React.createElement('span', { style: { color: '#c9cdd4' } }, '—');
  const pct = Math.round((th.progress || 0) * 100);
  if (!th.enabled) {
    return React.createElement('span', {
      style: { color: '#86909c', whiteSpace: 'nowrap' },
      title: '限速未启用 —— 这张卡当前是满速。5h 已用 ' + th.used + ' / ' + (th.limit || '∞') + '(' + pct + '%)',
    }, '满速');
  }
  const speedPct = Math.round((th.factor || 0) * 100);
  // 速度掉得越狠,颜色越警示
  const color = speedPct >= 90 ? '#00b42a' : (speedPct >= 60 ? '#ff7d00' : '#f56565');
  return React.createElement('span', {
    style: { color, whiteSpace: 'nowrap' },
    title: '当前速度 ' + speedPct + '%(保底 ' + (th.minPct || 0) + '%)\n'
      + '5h 已用 ' + th.used + ' / ' + (th.limit || '∞') + '(' + pct + '%)',
  }, speedPct + '%');
};

function formatQuota(n) {
  if (!n) return '0';
  return Number(n).toLocaleString();
}

// 状态文案 → 颜色。与表格「状态」列保持一致,卡片视图复用。
const STATUS_COLORS = {
  '待售': '#86909c',
  '待激活': '#1d7dfa', // 蓝色:已售等待买家首次调用
  '使用中': '#00b42a',
  '已禁用': '#86909c',
  '已过期': '#f56565',
  '已耗尽': '#f56565',
};

const EMPTY_FORM = { count: 1, key: '', remain_quota: 0, unlimited: false, remark: '' };

const AdminTokens = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // 桌面端表格分页:自己持有 currentPage。
  // Semi Table 在 dataSource 引用变化(本页每 5 秒自动刷新一次)时会重置 currentPage,
  // 翻到第 2 页会被弹回第 1 页;受控分页 + 自己切片可避免。
  const TABLE_PAGE_SIZE = 20;
  const { page: tablePage, pagedList, setPage: setTablePage } = usePagedRows(list, TABLE_PAGE_SIZE);

  // 窄屏(手机)用卡片视图替代表格:表格 11 列 + fixed 操作列在手机上会互相遮挡
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const [mobilePage, setMobilePage] = useState(1);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [soldFilter, setSoldFilter] = useState(''); // ''=全部, '0'=未售出, '1'=已售出
  const [planFilter, setPlanFilter] = useState(''); // ''=全部, '0'=未关联套餐, 正数=指定套餐 id

  // 套餐列表(用于生成令牌时选择套餐模板)
  const [plans, setPlans] = useState([]);

  // 弹窗两种模式:create / edit
  const [mode, setMode] = useState('create');
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [saving, setSaving] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState(null);

  // 行内标记/取消标记售出小弹窗: { open, row, targetStatus: 0|1, soldTo, saving }
  const [markDialog, setMarkDialog] = useState(null);
  // 批量删除确认弹窗: { open, ids, saving }
  const [batchDeleteDialog, setBatchDeleteDialog] = useState(null);
  // 续期弹窗: { open, row, months, saving }
  const [renewDialog, setRenewDialog] = useState(null);

  const load = (silent) => {
    if (!silent) setLoading(true); // 静默轮询不转圈,避免整表闪烁
    const params = [];
    if (soldFilter !== '') params.push('sold_status=' + soldFilter);
    if (planFilter !== '') params.push('plan_id=' + planFilter);
    const qs = params.length ? '?' + params.join('&') : '';
    API.get('/api/admin/tokens' + qs)
      .then((r) => setList(r.data.data || []))
      // 这个接口 5 秒自动刷新一次,漏掉 catch 就会因偶发网络抖动弹红框。
      // 静默轮询失败只记控制台(否则一次断网会连着弹 5 秒一个的提示),
      // 手动刷新才弹 Toast。
      .catch((e) => {
        if (silent) { console.warn('[AdminTokens] 静默刷新失败:', apiErrMsg(e)); return; }
        Toast.error(apiErrMsg(e));
      })
      .finally(() => setLoading(false));
    // 同时拉套餐列表(包含全部,包括下架的;筛选需要看到历史套餐,生成时再过滤 enabled)
    API.get('/api/admin/plans')
      .then((r) => setPlans(r.data.data || []))
      .catch(() => {});
  };
  useEffect(load, []);

  // 自动刷新:默认开,每 5 秒静默拉一次(不转圈、不动分页与勾选)。
  // 任一弹窗打开时暂停,避免编辑表单期间后台数据跳动;筛选变化时立即重载
  // (顺带修复了此前切换筛选不自动刷新、必须手点刷新按钮的问题)。
  const [autoRefresh, setAutoRefresh] = useState(true);
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const dialogOpen = modalOpen || markDialog?.open || batchDeleteDialog?.open || renewDialog?.open;
    if (dialogOpen) return undefined;
    const t = setInterval(() => load(true), 5000);
    return () => clearInterval(t);
  }, [autoRefresh, modalOpen, markDialog, batchDeleteDialog, renewDialog, soldFilter, planFilter]);

  const openNew = async () => {
    setMode('create');
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setNewKeyResult(null);
    setModalOpen(true);
    // 选默认套餐:优先用管理员在套餐页"设为默认"指定的;若该套餐已下架/被删,回退到第一个 enabled
    let chosen = null;
    try {
      const r = await API.get('/api/admin/settings');
      const prefId = parseInt(r.data.data?.default_token_plan_id || '0', 10) || 0;
      if (prefId > 0) {
        const pref = plans.find((p) => p.id === prefId);
        if (pref && pref.enabled) chosen = pref;
      }
    } catch { /* 忽略,继续走回退 */ }
    if (!chosen) chosen = plans.find((p) => p.enabled) || null;
    setSelectedPlanId(chosen ? String(chosen.id) : '');
    if (chosen) {
      setForm((prev) => ({
        ...prev,
        remain_quota: chosen.total_calls || 0,
        unlimited: false,
      }));
    }
  };

  // 选择套餐 → 自动填充余额(其他字段从套餐读)
  const applyPlan = (planId) => {
    setSelectedPlanId(planId);
    if (!planId) return;
    const p = plans.find((x) => String(x.id) === String(planId));
    if (!p) return;
    setForm((prev) => ({
      ...prev,
      remain_quota: p.total_calls || 0,
      unlimited: false,
    }));
  };

  const openEdit = (row) => {
    setMode('edit');
    setEditingId(row.id);
    setForm({
      key: row.key || '',
      remain_quota: row.remain_quota ?? 0,
      unlimited: !!row.unlimited,
      remark: row.remark || '',
    });
    setSelectedPlanId(row.plan_id ? String(row.plan_id) : '');
    setNewKeyResult(null);
    setModalOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (mode === 'create') {
        const r = await API.post('/api/admin/tokens', {
          ...form,
          count: Number(form.count) || 1,
          remark: form.remark?.trim() || '',
          plan_id: selectedPlanId ? Number(selectedPlanId) : 0,
        });
        setNewKeyResult(r.data.data);
        if (r.data.data.count > 1) Toast.success('已批量生成 ' + r.data.data.count + ' 个 Key');
        else Toast.success('已创建,完整 Key 只显示一次');
      } else {
        await API.put(`/api/admin/tokens/${editingId}`, {
          key: form.key?.trim() || '',
          remark: form.remark?.trim() || '',
          remain_quota: form.unlimited ? -1 : Number(form.remain_quota) || 0,
          unlimited: form.unlimited ? 1 : 0,
          plan_id: selectedPlanId ? Number(selectedPlanId) : 0,
        });
        Toast.success('已保存');
        setModalOpen(false);
      }
      load();
    } catch (e) {
      Toast.error(e.response?.data?.message || '操作失败');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row) => {
    const next = row.status === 1 ? 2 : 1;
    try {
      await API.put('/api/admin/tokens/' + row.id, { status: next });
      Toast.success(next === 1 ? '已启用' : '已禁用');
      load();
    } catch (e) {
      Toast.error(e.response?.data?.message || '操作失败');
    }
  };

  const remove = (row) => {
    Modal.confirm({
      title: '删除令牌?',
      content: '将删除令牌 sk-' + maskKey(row.key) + ',此操作不可恢复',
      onOk: async () => {
        try {
          await API.delete('/api/admin/tokens/' + row.id);
          Toast.success('已删除');
          load();
        } catch (e) {
          Toast.error(apiErrMsg(e));
        }
      },
    });
  };

  // 续期:延长有效期 + 本计费周期次数清零重算
  const doRenew = async () => {
    const { row, months } = renewDialog;
    setRenewDialog((d) => ({ ...d, saving: true }));
    try {
      const r = await API.post(`/api/admin/tokens/${row.id}/renew`, { months });
      Toast.success(`已续期 ${months} 个月,当前周期用量不变`);
      setRenewDialog(null);
      load();
    } catch (e) {
      Toast.error(e.response?.data?.message || '续期失败');
      setRenewDialog((d) => ({ ...d, saving: false }));
    }
  };

  const openRenew = (row) => setRenewDialog({ open: true, row, months: 1, saving: false });

  // 手机端卡片视图:每张令牌一张卡,信息分组展示,底部整行操作按钮(不再有 fixed 列遮挡)
  const renderMobileCards = () => {
    const PAGE_SIZE = 10;
    const totalPage = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
    const page = Math.min(mobilePage, totalPage);
    const pageRows = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const rowStyle = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#4e5969' };
    const cell = (label, value, color) => React.createElement('div', { style: { ...rowStyle, justifyContent: 'space-between' } },
      React.createElement('span', { style: { color: '#86909c', flexShrink: 0 } }, label),
      React.createElement('span', { style: { color: color || '#1f2329', textAlign: 'right' } }, value)
    );
    return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 10 } },
      pageRows.map((row) => {
        const exp = row.effective_expires_at;
        let expText = '永久';
        let expColor = '#1f2329';
        if (row.status === 5 && (!row.activated_at || row.activated_at === 0)) {
          expText = '—'; expColor = '#86909c';
        } else if (exp && exp > 0) {
          const d = new Date(exp);
          if (!isNaN(d.getTime())) {
            const remaining = exp - Date.now();
            expText = d.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
            expColor = remaining < 0 ? '#f56565' : (remaining < 7 * 86400000 ? '#ff7d00' : (remaining < 30 * 86400000 ? '#86909c' : '#1f2329'));
          }
        }
        const checked = selectedRowKeys.includes(row.id);
        return React.createElement('div', {
          key: row.id,
          style: {
            border: '1px solid #e5e6eb', borderRadius: 8, padding: '10px 12px',
            display: 'flex', flexDirection: 'column', gap: 8, background: '#fff',
          },
        },
          // 第一行:勾选框 + ID + 状态
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement(Checkbox, {
              checked,
              onChange: (v) => setSelectedRowKeys((prev) => (v ? prev.concat(row.id) : prev.filter((k) => k !== row.id))),
            }),
            React.createElement('span', { style: { fontSize: 12, color: '#86909c' } }, '#' + row.id),
            React.createElement('span', { style: { flex: 1 } }),
            React.createElement('span', { style: { color: STATUS_COLORS[row.status_text] || '#c9cdd4', fontSize: 12, fontWeight: 500 } }, row.status_text || '—')
          ),
          // Key 行(整行可点进详情)
          React.createElement('div', {
            style: { display: 'flex', alignItems: 'center', gap: 6 },
            onClick: () => navigate('/admin/tokens/' + row.id),
          },
            React.createElement('code', { style: { fontSize: 12, color: '#1d7dfa', wordBreak: 'break-all' } }, 'sk-' + maskKey(row.key)),
            React.createElement(Button, {
              size: 'small', theme: 'borderless', type: 'primary', style: { padding: '0 6px', fontSize: 11, flexShrink: 0 },
              onClick: (e) => {
                e.stopPropagation();
                navigator.clipboard.writeText('sk-' + row.key);
                Toast.success('已复制完整 Key');
              },
            }, '复制')
          ),
          // 信息区:两列网格
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' } },
            cell('套餐', row.plan_name || (row.plan_id ? '(套餐已删除)' : '未关联'), row.plan_id ? '#1f2329' : '#f56565'),
            cell('5h 限额', React.createElement('span', null,
              React.createElement('span', { style: { color: '#1d7dfa' } }, formatQuota(row.calls_5h || 0)),
              ' / ',
              React.createElement('span', null, row.effective_limit_5h ? formatQuota(row.effective_limit_5h) : '∞'))),
            cell('速度', React.createElement(ThrottleCell, { th: row.throttle })),
            cell('周限额', React.createElement('span', null,
              React.createElement('span', { style: { color: '#00b42a' } }, formatQuota(row.calls_week || 0)),
              ' / ',
              React.createElement('span', null, row.effective_limit_week ? formatQuota(row.effective_limit_week) : '∞'))),
            cell('月限额', React.createElement('span', null,
              React.createElement('span', { style: { color: '#8957e5' } }, formatQuota(row.used_quota || 0)),
              ' / ',
              React.createElement('span', null, row.unlimited ? '∞' : formatQuota((row.remain_quota || 0) + (row.used_quota || 0))))),
            cell('过期', expText, expColor)
          ),
          // 备注
          row.remark ? React.createElement('div', { style: { ...rowStyle, justifyContent: 'space-between', alignItems: 'flex-start' } },
            React.createElement('span', { style: { color: '#86909c', flexShrink: 0 } }, '备注'),
            React.createElement('span', { style: { color: '#4e5969', textAlign: 'right', wordBreak: 'break-all' } }, row.remark)
          ) : null,
          // 操作区:整行铺开,不再被 fixed 列遮挡
          React.createElement('div', {
            style: { display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', borderTop: '1px solid #f2f3f5', paddingTop: 8 },
            onClick: (e) => e.stopPropagation(),
          }, renderActions(row))
        );
      }),
      list.length > PAGE_SIZE ? React.createElement(Pagination, {
        total: list.length, pageSize: PAGE_SIZE, currentPage: page,
        onPageChange: (p) => setMobilePage(p),
        size: 'small', style: { alignSelf: 'center' },
      }) : null
    );
  };

  // 操作按钮组:表格「操作」列与手机卡片视图共用
  const renderActions = (row) => {
    // 待售(status=5 且 sold_status=0)的"启用/禁用"没有意义——只能等买家首次调用自动激活。
    // 这个位置直接换成「发货」:一键标记售出,免去"勾选 → 顶部批量标记"的流程
    const isPendingSale = row.status === 5 && row.sold_status === 0;
    const onToggle = (e) => {
      e.stopPropagation();
      toggleStatus(row);
    };
    return React.createElement('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' } },
      React.createElement(Button, { size: 'small', theme: 'borderless', type: 'primary', style: { padding: '0 6px' }, onClick: (e) => { e.stopPropagation(); openEdit(row); } }, '编辑'),
      React.createElement('span', { style: { color: '#c9cdd4', userSelect: 'none' } }, '|'),
      React.createElement(Button, { size: 'small', theme: 'borderless', type: 'primary', style: { padding: '0 6px' }, onClick: (e) => { e.stopPropagation(); openRenew(row); } }, '续期'),
      React.createElement('span', { style: { color: '#c9cdd4', userSelect: 'none' } }, '|'),
      isPendingSale
        ? React.createElement(Button, {
            size: 'small', theme: 'borderless', type: 'warning',
            style: { padding: '0 6px', color: '#ff7d00', fontWeight: 600 },
            onClick: (e) => {
              e.stopPropagation();
              setMarkDialog({ open: true, row, soldTo: '', saving: false });
            },
          }, '发货')
        : React.createElement(Button, { size: 'small', theme: 'borderless', type: 'primary', style: { padding: '0 6px' }, onClick: onToggle }, row.status === 1 ? '禁用' : '启用'),
      React.createElement('span', { style: { color: '#c9cdd4', userSelect: 'none' } }, '|'),
      React.createElement(Button, { size: 'small', theme: 'borderless', type: 'danger', style: { padding: '0 6px' }, onClick: (e) => { e.stopPropagation(); remove(row); } }, '删除')
    );
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: 'Key', dataIndex: 'key', width: 260,
      render: (k, row) => React.createElement('div', {
        style: { display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' },
        onClick: (e) => {
          e.stopPropagation();
          navigate('/admin/tokens/' + row.id);
        },
      },
        React.createElement('code', {
          style: { fontSize: 12, color: '#1d7dfa', whiteSpace: 'nowrap', background: 'transparent', cursor: 'pointer' },
          title: '点击查看日志',
        }, 'sk-' + maskKey(k)),
        React.createElement(Button, {
          size: 'small', theme: 'borderless', type: 'primary',
          style: { padding: '0 8px', fontSize: 11 },
          onClick: (e) => {
            e.stopPropagation();
            navigator.clipboard.writeText('sk-' + k);
            Toast.success('已复制完整 Key');
          },
        }, '复制'),
      ),
    },
    {
      title: '状态', dataIndex: 'status_text', width: 75,
      render: (text) => {
        // 后端 SQL 投影出来的业务状态文案:待售/待激活/使用中/已禁用/已过期/已耗尽
        return React.createElement('span', { style: { color: STATUS_COLORS[text] || '#c9cdd4', whiteSpace: 'nowrap' } }, text || '—');
      },
    },
    {
      title: '套餐', dataIndex: 'plan_name', width: 120,
      render: (name, row) => React.createElement('div', { style: { fontSize: 12, lineHeight: 1.4 } },
        React.createElement('div', {
          style: { color: row.plan_id ? '#1f2329' : '#f56565', fontWeight: 500 },
        }, name || (row.plan_id ? '(套餐已删除)' : '未关联')),
        row.plan_enabled === 0 && React.createElement('div', { style: { fontSize: 11, color: '#ff7d00' } }, '套餐已下架'),
      ),
    },
    {
      title: '5h 限额', width: 95,
      render: (_, row) => React.createElement('span', { style: { color: '#52525b', whiteSpace: 'nowrap' } },
        React.createElement('span', { style: { color: '#1d7dfa' } }, formatQuota(row.calls_5h || 0)),
        ' / ',
        React.createElement('span', null, row.effective_limit_5h ? formatQuota(row.effective_limit_5h) : '∞')
      ),
    },
    {
      // 限速速度:这张卡当前能跑多快(相对满速的百分比)。
      // 5h 用量 0/1000 → 100%(满速);用量满 → 降到保底。
      title: '速度', width: 80,
      render: (_, row) => React.createElement(ThrottleCell, { th: row.throttle }),
    },
    {
      title: '周限额', width: 95,
      render: (_, row) => React.createElement('span', { style: { color: '#52525b', whiteSpace: 'nowrap' } },
        React.createElement('span', { style: { color: '#00b42a' } }, formatQuota(row.calls_week || 0)),
        ' / ',
        React.createElement('span', null, row.effective_limit_week ? formatQuota(row.effective_limit_week) : '∞')
      ),
    },
    {
      // 一个计费周期(套餐天数,通常按月理解)内的总次数限额。
      // 格式与左侧 5h / 周限额保持一致:已用 / 限额,无限令牌限额位显示 ∞。
      // used_quota 会被 rollPeriod 在周期结束时清零,所以它天然就是"本周期已用"。
      title: '月限额',
      width: 105,
      render: (_, row) => React.createElement('span', { style: { color: '#52525b', whiteSpace: 'nowrap' } },
        React.createElement('span', { style: { color: '#8957e5' } }, formatQuota(row.used_quota || 0)),
        ' / ',
        // 无限令牌的 remain_quota 存的是 -1,不能参与求和
        React.createElement('span', null, row.unlimited ? '∞' : formatQuota((row.remain_quota || 0) + (row.used_quota || 0)))
      ),
    },
    {
      title: '过期', width: 140,
      render: (_, row) => {
        if (row.status === 5 && (!row.activated_at || row.activated_at === 0)) {
          return React.createElement('span', { style: { color: '#86909c' } }, '—');
        }
        const exp = row.effective_expires_at;
        if (!exp || exp <= 0) {
          return React.createElement('span', { style: { color: '#86909c' } }, '永久');
        }
        const d = new Date(exp);
        if (isNaN(d.getTime())) return React.createElement('span', null, '—');
        const now = Date.now();
        const remaining = exp - now;
        // 颜色规则:已过期 → 红;< 7 天 → 橙;< 30 天 → 灰;其余 → 黑
        const color = remaining < 0
          ? '#f56565'
          : (remaining < 7 * 86400000 ? '#ff7d00'
          : (remaining < 30 * 86400000 ? '#86909c' : '#1f2329'));
        return React.createElement('span', { style: { color, whiteSpace: 'nowrap' } },
          d.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
        );
      },
    },
    {
      title: '备注', dataIndex: 'remark', width: 180,
      render: (r) => {
        if (!r) return React.createElement('span', { style: { color: '#c9cdd4' } }, '—');
        // 内容过长时省略号截断(保留 title 看完整内容),不换行
        return React.createElement('span', {
          title: r,
          style: {
            display: 'inline-block', maxWidth: 160,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            verticalAlign: 'middle', color: '#4e5969',
          },
        }, r);
      },
    },
    {
      title: '操作', width: 210, fixed: 'right',
      render: (_, row) => renderActions(row),
    },
  ];

  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const showNewKey = isCreate && newKeyResult;

  const selectedCount = selectedRowKeys.length;
  const hasSelection = selectedCount > 0;

  return React.createElement('div', null,
    // 标题行(左: 令牌管理 / 右: + 生成令牌)
    React.createElement('div', { style: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: 16, gap: 8, flexWrap: 'wrap',
    } },
      React.createElement('h2', { style: { fontSize: 18, fontWeight: 600, margin: 0 } }, '令牌管理'),
      React.createElement(Button, { type: 'primary', theme: 'solid', onClick: openNew }, '+ 生成令牌'),
    ),

    // 筛选条件(左) + 操作按钮(右)
    React.createElement('div', { style: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 8, marginBottom: 12, flexWrap: 'wrap',
    } },
      // 左:筛选区
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' } },
        React.createElement(Select, {
          size: 'small', style: { width: isMobile ? 110 : 140 },
          value: soldFilter,
          onChange: (v) => { setSoldFilter(v); setMobilePage(1); setTablePage(1); },
          optionList: [
            { value: '', label: '全部状态' },
            { value: '0', label: '未售出' },
            { value: '1', label: '已售出' },
          ],
        }),
        React.createElement(Select, {
          size: 'small', style: { width: isMobile ? 130 : 200 },
          value: planFilter,
          onChange: (v) => { setPlanFilter(v); setMobilePage(1); setTablePage(1); },
          optionList: [
            { value: '', label: '全部套餐' },
            { value: '0', label: '未关联套餐' },
            ...plans.map((p) => ({ value: String(p.id), label: p.name + (p.enabled ? '' : ' (已下架)') })),
          ],
        }),
        React.createElement(Button, { size: 'small', theme: 'borderless', type: 'primary', onClick: () => load() }, '刷新'),
        React.createElement('span', {
          title: '每 5 秒自动刷新列表', style: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#86909c' },
        },
          React.createElement(Switch, { size: 'small', checked: autoRefresh, onChange: (v) => setAutoRefresh(v) }),
          '自动',
        ),
        hasSelection ? React.createElement('span', { style: { fontSize: 12, color: '#1d7dfa', marginLeft: 4 } }, `已选 ${selectedCount} 项`) : null,
      ),
      // 右:操作区
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' } },
        hasSelection ? React.createElement(Button, {
          size: 'small', type: 'tertiary',
          onClick: () => setBatchDeleteDialog({ open: true, ids: selectedRowKeys.slice(), saving: false }),
        }, `批量删除(${selectedCount})`) : null,
        hasSelection ? React.createElement(Button, {
          size: 'small', type: 'tertiary',
          onClick: () => window.open('/api/admin/tokens/export?ids=' + encodeURIComponent(selectedRowKeys.join(',')), '_blank'),
        }, `导出CSV(${selectedCount})`) : null,
      ),
    ),
    React.createElement('div', { style: { background: '#fff', borderRadius: 10, padding: 8, border: '1px solid #e5e6eb' } },
      React.createElement(Spin, { spinning: loading },
        isMobile
          // 手机端:卡片视图。表格 11 列 + fixed 操作列在窄屏会互相遮挡、Key 被压没
          ? renderMobileCards()
          : React.createElement(Table, {
              size: 'small', rowKey: 'id', columns, dataSource: pagedList,
              scroll: { x: 1360 },
              rowSelection: {
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
                // 全部行可勾选:已售出的卡也需要批量删除/批量导出
              },
              onRow: (record) => ({
                onClick: (e) => {
                  // 复选框 / 操作列 / 复制按钮 已在内部 stopPropagation,这里兜底再判一次 target
                  const tag = (e.target && e.target.tagName) || '';
                  if (tag === 'INPUT' || tag === 'BUTTON' || tag === 'LABEL') return;
                  navigate('/admin/tokens/' + record.id);
                },
                style: { cursor: 'pointer' },
              }),
              pagination: {
                currentPage: tablePage,
                pageSize: TABLE_PAGE_SIZE,
                total: list.length,
                hideOnSinglePage: true,
                onPageChange: setTablePage,
              },
            })
      )
    ),
    React.createElement(Modal, {
      title: isCreate ? '生成新令牌' : '编辑令牌',
      visible: modalOpen,
      onCancel: () => setModalOpen(false),
      width: 600,
      footer: showNewKey
        ? React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 16, borderTop: '1px solid #e5e7eb' } },
            React.createElement(Button, { onClick: () => setModalOpen(false) }, '关闭'),
            newKeyResult.count > 1
              ? React.createElement(Button, { type: 'primary', theme: 'solid', onClick: () => {
                  const all = newKeyResult.keys.map((k) => k.full).join('\n');
                  navigator.clipboard.writeText(all); Toast.success('已复制全部 ' + newKeyResult.count + ' 个');
                }}, '复制全部 Key')
              : React.createElement(Button, { type: 'primary', theme: 'solid', onClick: () => {
                  navigator.clipboard.writeText(newKeyResult.keys?.[0]?.full || newKeyResult.key); Toast.success('已复制');
                }}, '复制 Key')
          )
        : React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 16, borderTop: '1px solid #e5e7eb' } },
            React.createElement(Button, { onClick: () => setModalOpen(false) }, '取消'),
            React.createElement(Button, { type: 'primary', theme: 'solid', onClick: save, loading: saving }, isCreate ? '生成' : '保存'),
          ),
    },
      showNewKey
        ? React.createElement('div', null,
            newKeyResult.count > 1
              ? React.createElement('div', null,
                  React.createElement('div', { style: { marginBottom: 12, fontSize: 13, color: '#4e5969' } }, '✓ 已生成 ' + newKeyResult.count + ' 个 Key(完整 Key 只显示一次,请立即复制!)'),
                  React.createElement('textarea', {
                    readOnly: true,
                    rows: Math.min(12, newKeyResult.count + 2),
                    style: { width: '100%', fontFamily: 'ui-monospace, monospace', fontSize: 12, padding: 10, border: '1px solid #ffd591', borderRadius: 8, background: '#fff7e6', resize: 'vertical', color: '#ad6800', fontWeight: 600 },
                    value: newKeyResult.keys.map((k) => k.full).join('\n'),
                    onClick: function(e){ e.target.select(); navigator.clipboard.writeText(e.target.value); Toast.success('已全部复制'); },
                  }),
                  React.createElement('div', { style: { fontSize: 12, color: '#86909c', marginTop: 6 } }, '点击文本框可一键复制全部 Key')
                )
              : React.createElement('div', null,
                  React.createElement('div', { style: { marginBottom: 12, fontSize: 13, color: '#4e5969' } }, '令牌已创建成功,完整 Key 只显示这一次,请立即复制保存!'),
                  React.createElement('div', { style: { background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8, padding: 14, marginBottom: 14 } },
                    React.createElement('code', { style: { color: '#ad6800', fontSize: 13, fontWeight: 600, wordBreak: 'break-all', fontFamily: 'ui-monospace, Menlo, monospace', background: 'transparent' } }, (newKeyResult.keys && newKeyResult.keys[0] && newKeyResult.keys[0].full) || newKeyResult.key)
                  )
                )
          )
        : React.createElement('div', null,
            // 生成数量(仅创建模式)
            isCreate && React.createElement('div', { className: 'form-row', style: { display: 'flex', alignItems: 'flex-start', marginBottom: 12, background: '#fafbfc', padding: 10, borderRadius: 6, border: '1px solid #e5e6eb' } },
              React.createElement('label', { style: { width: 110, fontSize: 13, color: '#4e5969', paddingTop: 6, flexShrink: 0 } }, '生成数量'),
              React.createElement('div', { style: { flex: 1 } },
                React.createElement(InputNumber, {
                  min: 1, max: 500, step: 10,
                  value: Number(form.count) || 1,
                  onChange: (v) => setForm({ ...form, count: v }),
                  style: { width: 140 },
                }),
                React.createElement('span', { style: { fontSize: 12, color: '#86909c', marginLeft: 8 } }, form.count > 1 ? '将一次生成 ' + form.count + ' 个相同配置的 Key(默认未售出)' : '= 单次生成 1 个'),
              )
            ),
                        plans.length > 0 && React.createElement('div', { className: 'form-row', style: { display: 'flex', alignItems: 'flex-start', marginBottom: 12, background: '#f4f8ff', padding: 10, borderRadius: 6, border: '1px solid #dbe8ff' } },
              React.createElement('label', { style: { width: 110, fontSize: 13, color: '#1d7dfa', paddingTop: 6, flexShrink: 0, fontWeight: 500 } }, '所属套餐 *'),
              React.createElement('div', { style: { flex: 1, minWidth: 0 } },
                React.createElement(Select, {
                  style: { width: '100%', minWidth: 0 },
                  value: selectedPlanId,
                  onChange: applyPlan,
                  placeholder: '必选,令牌的所有限额/过期/模型限制都从套餐读取',
                  optionList: [
                    ...plans.filter((p) => isCreate ? p.enabled : true).map((p) => ({
                      value: String(p.id),
                      label: `${p.name}${p.enabled ? '' : ' (已下架)'} · ￥${(p.price_cents / 100).toFixed(2)}/月 · ${p.total_calls}次 · 5h ${p.limit_5h || '∞'} · ${p.expired_days > 0 ? p.expired_days + '天' : '永久'}`,
                    })),
                  ],
                }),
                React.createElement('div', {
                  className: 'form-hint',
                  title: '选套餐后,默认余额 = 套餐额度。修改套餐,所有令牌自动跟随(过期时间、5h/周限额、模型限制、套餐额度)',
                }, '选套餐后默认余额 = 套餐额度,改套餐所有令牌自动跟随'),
              )
            ),
            React.createElement('div', { className: 'form-row', style: { display: 'flex', alignItems: 'flex-start', marginBottom: 12 } },
              React.createElement('label', { style: { width: 110, fontSize: 13, color: '#4e5969', paddingTop: 6, flexShrink: 0 } }, '调用次数'),
              React.createElement('div', { style: { flex: 1 } },
                React.createElement('div', { style: { display: 'flex', gap: 8, alignItems: 'center' } },
                  React.createElement(InputNumber, {
                    disabled: form.unlimited, min: 0, step: 1000,
                    value: Number(form.remain_quota) || 0,
                    onChange: (v) => setForm({ ...form, remain_quota: v }),
                    style: { flex: 1 },
                  }),
                  React.createElement(Switch, { checked: form.unlimited, onChange: (v) => setForm({ ...form, unlimited: v }) }),
                  React.createElement('span', { style: { fontSize: 12, color: '#86909c' } }, '无限'),
                ),
                React.createElement('div', {
                  className: 'form-hint',
                  title: '每次成功调用按模型倍率扣费(最低 1,无限时不扣),0 = 套餐用尽。选套餐后默认 = 套餐额度。输入 token 超过渠道配置的上下文窗口时按溢价倍率扣费',
                }, '按模型倍率扣费(最低 1,无限时不扣),0 = 套餐用尽'),
              )
            ),
            isEdit && React.createElement('div', { className: 'form-row', style: { display: 'flex', alignItems: 'flex-start', marginBottom: 12 } },
              React.createElement('label', { style: { width: 110, fontSize: 13, color: '#4e5969', paddingTop: 6, flexShrink: 0 } }, 'API Key'),
              React.createElement('div', { style: { flex: 1 } },
                React.createElement(Input, {
                  value: form.key,
                  onChange: (v) => setForm({ ...form, key: v }),
                  placeholder: 'sk- 前缀可选,保存时自动去掉',
                  mode: 'password',
                }),
                React.createElement('div', { className: 'form-hint' }, '改了 Key 后,旧 Key 立即失效,需要把新 Key 给调用方')
              )
            ),
            React.createElement('div', { className: 'form-row', style: { display: 'flex', alignItems: 'flex-start', marginBottom: 12 } },
              React.createElement('label', { style: { width: 110, fontSize: 13, color: '#4e5969', paddingTop: 6, flexShrink: 0 } }, '备注'),
              React.createElement('div', { style: { flex: 1 } },
                React.createElement(Input, {
                  value: form.remark,
                  onChange: (v) => setForm({ ...form, remark: v }),
                  placeholder: '可选,例如 alice-2025-09、闲鱼买家张三',
                })
              )
            )
          )
    ),

    // 行内「发货」弹窗:一键标记售出,买家/订单号可选写入备注列。
    // 只做标记售出,没有取消售出功能——发错卡直接删除令牌
    markDialog && markDialog.open ? React.createElement(Modal, {
      key: 'mark-dialog',
      title: '发货',
      visible: markDialog.open,
      onCancel: () => setMarkDialog(null),
      width: 440,
      okText: '确认发货',
      okButtonProps: { loading: !!markDialog.saving },
      cancelText: '关闭',
      onOk: async () => {
        setMarkDialog((d) => d ? Object.assign({}, d, { saving: true }) : d);
        const id = markDialog.row.id;
        try {
          await API.post('/api/admin/tokens/mark-sold', {
            ids: [id],
            sold_to: markDialog.soldTo || '',
          });
          Toast.success('已发货(标记为已售出)');
          setMarkDialog(null);
          setSelectedRowKeys([]);
          load();
        } catch (e) {
          Toast.error('操作失败');
          setMarkDialog((d) => d ? Object.assign({}, d, { saving: false }) : d);
        }
      },
    },
      React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 13, color: '#1d2129', marginBottom: 12 } },
              '发货后标记为已售出,状态变为「待激活」,买家首次调用后自动激活'
            ),
            React.createElement('div', { style: { marginBottom: 8, fontSize: 13, color: '#4e5969' } }, '买家/订单号(可选,自动写入备注列)'),
            React.createElement(Input, {
              value: markDialog.soldTo || '',
              onChange: (v) => setMarkDialog((d) => d ? Object.assign({}, d, { soldTo: v }) : d),
              placeholder: '例如:小鱼儿 / 订单 20250916-001',
              maxLength: 64,
              showClear: true,
            })
          )
    ) : null,

    // 批量删除确认弹窗
    batchDeleteDialog && batchDeleteDialog.open ? React.createElement(Modal, {
      key: 'batch-delete-dialog',
      title: '确认批量删除',
      visible: batchDeleteDialog.open,
      onCancel: () => setBatchDeleteDialog(null),
      width: 440,
      okText: '确认删除',
      okButtonProps: { loading: !!batchDeleteDialog.saving, type: 'danger' },
      cancelText: '取消',
      onOk: async () => {
        setBatchDeleteDialog((d) => d ? Object.assign({}, d, { saving: true }) : d);
        try {
          const r = await API.post('/api/admin/tokens/batch-delete', { ids: batchDeleteDialog.ids });
          Toast.success('已删除 ' + (r.data?.data?.deleted ?? batchDeleteDialog.ids.length) + ' 个令牌');
          setBatchDeleteDialog(null);
          setSelectedRowKeys([]);
          load();
        } catch (e) {
          Toast.error(e.response?.data?.message || '删除失败');
          setBatchDeleteDialog((d) => d ? Object.assign({}, d, { saving: false }) : d);
        }
      },
    },
      React.createElement('div', { style: { fontSize: 13, color: '#1d2129' } },
        React.createElement('div', { style: { marginBottom: 8 } },
          '将永久删除 ',
          React.createElement('b', { style: { color: '#f56565' } }, batchDeleteDialog.ids.length),
          ' 个令牌及其调用日志,此操作不可恢复。'
        ),
        React.createElement('div', { style: { fontSize: 12, color: '#86909c' } }, '请确认无重要数据后再操作。')
      )
    ) : null,

    // 续期弹窗
    renewDialog && renewDialog.open ? React.createElement(Modal, {
      key: 'renew-dialog',
      title: '令牌续期',
      visible: renewDialog.open,
      onCancel: () => setRenewDialog(null),
      width: 460,
      okText: '确认续期',
      okButtonProps: { loading: !!renewDialog.saving, type: 'primary' },
      cancelText: '取消',
      onOk: doRenew,
    },
      React.createElement('div', { style: { fontSize: 13, color: '#1d2129', lineHeight: 1.9 } },
        React.createElement('div', null,
          '令牌 ',
          React.createElement('code', { style: { fontSize: 12, color: '#1d7dfa' } }, 'sk-' + maskKey(renewDialog.row.key)),
          ' 续期'
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0' } },
          React.createElement('span', { style: { fontSize: 13 } }, '续期时长'),
          React.createElement(InputNumber, {
            min: 1, max: 12, defaultValue: renewDialog.months,
            style: { width: 100 },
            onChange: (v) => setRenewDialog((d) => (d ? Object.assign({}, d, { months: v || 1 }) : d)),
          }),
          React.createElement('span', { style: { fontSize: 13 } }, '个月')
        ),
        React.createElement('div', { style: {
          padding: 10, marginTop: 6, background: '#f7f8fa',
          borderRadius: 4, fontSize: 12, color: '#4e5969',
        } },
          React.createElement('div', null, '当前到期：' + (
            renewDialog.row.effective_expires_at > 0
              ? new Date(renewDialog.row.effective_expires_at).toLocaleString('zh-CN')
              : '永久/未计时'
          )),
          renewDialog.row.unlimited
            ? React.createElement('div', { style: { marginTop: 4 } }, '次数：无限额度令牌，仅延长有效期')
            : React.createElement('div', { style: { marginTop: 4 } },
                '当前周期用量（保持不变）：' + formatQuota(renewDialog.row.used_quota) + ' / ' +
                formatQuota((renewDialog.row.remain_quota || 0) + (renewDialog.row.used_quota || 0))
              ),
          React.createElement('div', { style: { marginTop: 6, color: '#86909c' } },
            '续期只延长总有效期，不影响本周期用量。到期进入新周期时次数自动清零重新计费。'
          ),
        )
      )
    ) : null
  );
};

export default AdminTokens;
